import React, { Component } from 'react';
import PropTypes from 'prop-types';
import GoogleMap from 'google-map-react';
import ColorPoint from './Points/ColorPoint';
import SpotPoint from './Points/SpotPoint';
import CrosshairPoint from './Points/CrosshairPoint';
import config from 'config';
import utils from 'utils';
import './styles.scss';
import actions from 'actions';
import { getGoogleMapsLocaleForCountry } from 'utils/googleMapsLocale';

const escapeHtml = value =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildPoiInfoWindowContent = place => {
  const name = escapeHtml(place.name);
  const address = place.address ? escapeHtml(place.address) : '';

  // The title keeps the `gm-title` class so SpotForm's existing
  // `document.querySelector('.Map .gm-title')` auto-fill selector keeps working,
  // now reading our localized name instead of Google's browser-language one.
  return `
    <div class="poi-info-window-local" dir="auto" style="font-family: Roboto, Arial, sans-serif; min-width: 180px; max-width: 280px; line-height: 1.35;">
      <div class="gm-title" style="font-size: 15px; font-weight: 600; color: #202124;">${name}</div>
      ${address ? `<div style="margin-top: 4px; font-size: 13px; color: #5f6368;">${address}</div>` : ''}
    </div>
  `;
};

class Map extends Component {
  state = {
    center: this.props.center,
    zoom: this.props.zoom,
    points: this.props.points,
    bounds: {
      ne: {
        lat: 0,
        lng: 0,
      },
      sw: {
        lat: 0,
        lng: 0,
      },
    },
    searchText: '',
    suggestions: [],
  };

  UNSAFE_componentWillReceiveProps({ center, zoom, points, preserveViewportOnUpdate }) {
    const nextState = { points };

    if (!preserveViewportOnUpdate) {
      nextState.center = center;
      nextState.zoom = zoom;
    }

    this.setState(nextState);
  }

  shouldComponentUpdate(newProp, newState) {
    const sameZoom = newProp.zoom === this.state.zoom;
    const sameActions = newProp.actions === this.props.actions;
    const sameCenter = JSON.stringify(newProp.center) === JSON.stringify(this.state.center);
    const sameQuery = newState.searchText === this.state.searchText;

    if (!sameZoom || !sameActions || !sameCenter || !sameQuery) {
      return true;
    }

    const sameSuggestions =
      JSON.stringify(newState.suggestions) === JSON.stringify(this.state.suggestions);

    if (!sameSuggestions) {
      return true;
    }

    const samePoints = JSON.stringify(newProp.points) === JSON.stringify(this.state.points);

    return !samePoints;
  }

  _onMapChange = data => {
    this.setState(
      {
        zoom: data.zoom,
        bounds: data.bounds,
      },
      () => {
        if (typeof this.props.onChange === 'function') {
          this.props.onChange(data);
        }
      }
    );
  };

  onGoogleMapsReady = async ({ map, maps }) => {
    const { favouriteRegions } = await actions.favouriteRegions.getAll();

    favouriteRegions.forEach(favouriteRegion => {
      const paths = [
        { lat: favouriteRegion.ne.lat, lng: favouriteRegion.sw.lng },
        { lat: favouriteRegion.sw.lat, lng: favouriteRegion.sw.lng },
        { lat: favouriteRegion.sw.lat, lng: favouriteRegion.ne.lng },
        { lat: favouriteRegion.ne.lat, lng: favouriteRegion.ne.lng },
        { lat: favouriteRegion.ne.lat, lng: favouriteRegion.sw.lng },
      ];

      new maps.Polygon({
        paths,
        strokeColor: favouriteRegion.user.subscriptionLevel || 'blue',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#000',
        fillOpacity: 0,
        clickable: false,
      }).setMap(map);
    });

    this.googleMap = map;

    if (this.poiClickListener) {
      this.poiClickListener.remove();
    }

    this.poiClickListener = maps.event.addListener(map, 'click', this.handleNativeMapClick);
  };

  componentWillUnmount() {
    clearTimeout(this.timeout);

    if (this.poiClickListener) {
      this.poiClickListener.remove();
    }

    if (this.poiInfoWindow) {
      this.poiInfoWindow.close();
      this.poiInfoWindow = null;
    }
  }

  getLocalPlaceDetails = async ({ placeId, location }) => {
    const { countryCode } = location ? await actions.google.getPlace(location.lat, location.lng) : {};
    const locale = getGoogleMapsLocaleForCountry(countryCode);
    const placeOptions = {
      id: placeId,
      requestedLanguage: locale.language,
    };

    if (locale.region) {
      placeOptions.requestedRegion = locale.region;
    }

    const place = new window.google.maps.places.Place(placeOptions);

    await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });

    return {
      placeId,
      name: place.displayName,
      address: place.formattedAddress,
      language: place.displayNameLanguageCode || locale.language,
      region: locale.region,
      lat: place.location ? place.location.lat() : location && location.lat,
      lng: place.location ? place.location.lng() : location && location.lng,
    };
  };

  handleNativeMapClick = async event => {
    if (
      !event ||
      !event.placeId ||
      !(window.google && window.google.maps && window.google.maps.places)
    ) {
      return;
    }

    // Google's default POI info window always renders in the viewer's browser
    // language (e.g. Spanish), ignoring the locale of the country being
    // analyzed. Suppress it and render our own localized window instead.
    if (typeof event.stop === 'function') {
      event.stop();
    }

    const location = event.latLng
      ? {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        }
      : null;

    try {
      const place = await this.getLocalPlaceDetails({ placeId: event.placeId, location });

      if (!place.name) {
        return;
      }

      this.showPoiInfoWindow(place);

      if (typeof this.props.onPoiClick === 'function') {
        this.props.onPoiClick(place);
      }
    } catch (error) {
      console.error('Local POI fetch failed:', error);
    }
  };

  showPoiInfoWindow = place => {
    if (!this.googleMap || !(window.google && window.google.maps && window.google.maps.InfoWindow)) {
      return;
    }

    if (!this.poiInfoWindow) {
      this.poiInfoWindow = new window.google.maps.InfoWindow({
        disableAutoPan: this.props.disablePoiAutoPan,
      });
    }

    this.poiInfoWindow.setContent(buildPoiInfoWindowContent(place));

    if (place.lat != null && place.lng != null) {
      this.poiInfoWindow.setPosition({ lat: place.lat, lng: place.lng });
    }

    this.poiInfoWindow.open({ map: this.googleMap });
  };

  timeout = null;
  handleSearchChange = event => {
    clearTimeout(this.timeout);

    const text = event.target.value;

    this.setState({ searchText: text });

    if (!text) {
      this.setState({ suggestions: [] });
      return;
    }

    this.timeout = setTimeout(async () => {
      if (!(window.google && window.google.maps && window.google.maps.places)) {
        return;
      }

      try {
        const { suggestions } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: text,
          });

        this.setState({
          suggestions: (suggestions || [])
            .map(s => s.placePrediction)
            .filter(Boolean)
            .slice(0, 5)
            .map(prediction => ({
              place_id: prediction.placeId,
              description: prediction.text.toString(),
            })),
        });
      } catch (error) {
        console.error('Autocomplete failed:', error);
      }
    }, 500);
  };

  handleSuggestionClick = async suggestion => {
    if (!(window.google && window.google.maps && window.google.maps.places)) {
      return;
    }

    try {
      const place = new window.google.maps.places.Place({ id: suggestion.place_id });
      await place.fetchFields({ fields: ['location', 'viewport'] });

      if (!place.location) {
        return;
      }

      this.setState({
        center: { lat: place.location.lat(), lng: place.location.lng() },
        zoom: 18,
        searchText: '',
        suggestions: [],
      });
    } catch (error) {
      console.error('Place fetch failed:', error);
    }
  };

  render() {
    const { onPointClick, onPointHover, onSpotClick, selectedPhoto, selectedSpot, hideSpots } =
      this.props;
    const points = this.props.points.filter(
      x => x.id === selectedPhoto.id || utils.coors.contained(x, this.state.bounds)
    );

    let subPoints = [];

    const currentPoint = selectedPhoto;
    if (currentPoint && currentPoint.detective) {
      subPoints = currentPoint.detective.map(x => ({
        ...x,
        id: x.whoId,
        lat: x.position.latitude,
        lng: x.position.longitude,
        type: 'DETECTIVE',
        pointType: 'subPoint',
        _status: 99,
      }));
    }

    return (
      <div className="Map">
        <div className="actions">{this.props.actions}</div>

        <GoogleMap
          resetBoundsOnResize
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={this.onGoogleMapsReady}
          center={this.state.center}
          zoom={this.state.zoom}
          onChange={this._onMapChange}
          onClick={this.props.onClick}
          options={{
            tilt: 0,
            fullscreenControl: true,
            streetViewControl: true,
            mapTypeId: 'hybrid',
            styles: [
              {
                featureType: 'all',
                elementType: 'labels',
                stylers: [
                  {
                    visibility: '#on',
                  },
                ],
              },
            ],
          }}
          bootstrapURLKeys={{
            key: config.googleMaps.apiKey,
            libraries: 'places',
          }}
        >
          {subPoints.map(point => (
            <ColorPoint
              key={point.id}
              isSelected={point.id === selectedPhoto.id}
              {...point}
              onClick={() => onPointClick(point)}
            />
          ))}

          {points.map(point => {
            if (point.pointType === 'photo') {
              return (
                <ColorPoint
                  key={point.id}
                  {...point}
                  isSelected={point.id === selectedPhoto.id}
                  onClick={() => onPointClick(point)}
                  onHover={() => onPointHover(point)}
                />
              );
            }

            if (point.pointType === 'userPhoto') {
              return (
                <ColorPoint
                  key={point.id}
                  {...point}
                  isSelected={point.id === selectedPhoto.id}
                  onClick={() => onPointClick(point)}
                  onHover={() => onPointHover(point)}
                />
              );
            }

            if (point.pointType === 'crosshair') {
              return <CrosshairPoint key="crosshair" {...point} />;
            }

            if (point.pointType === 'spot' && !hideSpots) {
              return (
                <SpotPoint
                  key={point.id}
                  {...point}
                  isSelected={point.id === selectedSpot.id}
                  onClick={() => onSpotClick(point)}
                />
              );
            }

            if (point.pointType === 'area') {
              return (
                <ColorPoint key={point.id} {...point} isSelected={point.id === selectedPhoto.id} />
              );
            }

            if (point.pointType === 'citytour') {
              return (
                <ColorPoint key={point.id} {...point} isSelected={point.id === selectedPhoto.id} />
              );
            }

            return null;
          })}
        </GoogleMap>

        <div className="count">
          Visibles: {points.length} | Uns+Users:{' '}
          {this.props.points.length - this.props.spots.length - this.props.cityTours.length} |
          Spots: {this.props.spots.length} | CityTours: {this.props.cityTours.length}
        </div>

        <div className="searcher" style={{ opacity: this.state.searchText ? 1 : 0.8 }}>
          <input
            type="text"
            value={this.state.searchText}
            onChange={this.handleSearchChange}
            placeholder="Search places..."
          />
          {this.state.suggestions.length > 0 && (
            <div className="suggestions">
              {this.state.suggestions.map(suggestion => (
                <div
                  key={suggestion.place_id}
                  onClick={() => this.handleSuggestionClick(suggestion)}
                  style={{ padding: '5px', cursor: 'pointer' }}
                >
                  {suggestion.description}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

Map.defaultProps = {
  center: { lat: 34.0442, lng: -118.2488 },
  zoom: 1,
  points: [],
  actions: [],
  onChange: () => {},
  onClick: () => {},
  onPointClick: () => {},
  onPointHover: () => {},
  onPoiClick: null,
  disablePoiAutoPan: false,
  preserveViewportOnUpdate: false,
};

Map.propTypes = {
  center: PropTypes.object,
  zoom: PropTypes.number,
  points: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.arrayOf(PropTypes.element),
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onPointClick: PropTypes.func,
  onPointHover: PropTypes.func,
  onPoiClick: PropTypes.func,
  disablePoiAutoPan: PropTypes.bool,
  preserveViewportOnUpdate: PropTypes.bool,
};

export default Map;
